import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import Modal from 'src/components/modal/Modal';
import {
  FamousShipmentDTO,
  OrderfulValidationError,
} from 'src/api/temp/famous/useFamousShipments';
import useSendEdi856Transaction from 'src/api/temp/famous/useSendEdi856Transaction';
import useSyncFamousShipment from 'src/api/temp/famous/useSyncFamousShipment';
import React, { useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Spinner } from 'src/components/ui/spinner';
import {
  HourglassIcon,
  ArrowsClockwiseIcon,
  CaretDownIcon,
  CaretUpIcon,
} from '@phosphor-icons/react';
import config from 'src/config';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { LoadingSpinner } from 'src/components/common/LoadingSpinner';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from 'src/context/view-mode.context';
import { isAdmin } from 'src/utils/user';
import { describeEdiDataPath } from 'src/utils/ediDataPath';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { T, Var, useGT } from 'gt-react';

interface Props {
  famousShipment: FamousShipmentDTO;
  onClose: (refetch?: boolean) => void;
  isFetching?: boolean;
}

// Separator the backend appends to edi856Error before the JSON-stringified
// validation errors. When we render those errors structurally, we trim the
// raw blob off the text message at this marker.
const VALIDATION_ERRORS_SUFFIX = ' Errors:';
// Custom JSON viewer to match the pattern used elsewhere in the codebase
interface JsonObject {
  [key: string]: JsonValue;
}

type JsonValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | JsonObject
  | Array<JsonValue>;

const JsonViewer = ({
  data,
  level = 0,
}: {
  data: JsonValue;
  level?: number;
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    // Expand first level by default
    root: level < 1,
  });

  if (data === null)
    return (
      <span style={{ color: '#999' }}>
        <T context="JSON viewer display of null value">null</T>
      </span>
    );
  if (data === undefined)
    return (
      <span style={{ color: '#999' }}>
        <T context="JSON viewer display of undefined value">undefined</T>
      </span>
    );

  if (typeof data !== 'object') {
    // Handle primitive values
    if (typeof data === 'string') {
      return (
        <span
          style={{
            color: '#009688',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          &quot;{data}&quot;
        </span>
      );
    }
    if (typeof data === 'number') {
      return <span style={{ color: '#1976d2' }}>{data}</span>;
    }
    if (typeof data === 'boolean') {
      return (
        <span style={{ color: '#ff9800' }}>
          {data ? (
            <T context="JSON viewer display of boolean true value">true</T>
          ) : (
            <T context="JSON viewer display of boolean false value">false</T>
          )}
        </span>
      );
    }
    return <span style={{ wordBreak: 'break-word' }}>{String(data)}</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return (
        <span>
          <T context="JSON viewer display of an empty array">[ ]</T>
        </span>
      );
    }

    return (
      <div style={{ paddingLeft: level > 0 ? 20 : 0, width: '100%' }}>
        <span
          onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          {expanded.root ? (
            <T context="collapse indicator icon for JSON viewer array node">
              ▼
            </T>
          ) : (
            <T context="expand indicator icon for JSON viewer array node">▶</T>
          )}{' '}
          <T>
            Array [<Var>{data.length}</Var>]
          </T>
        </span>

        {expanded.root && (
          <div style={{ paddingLeft: 20, width: '100%' }}>
            {data.map((item, i) => (
              <div key={i} style={{ width: '100%' }}>
                <span style={{ color: '#555', marginRight: 8 }}>[{i}]:</span>
                <JsonViewer data={item} level={level + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Object
  const keys = Object.keys(data);
  if (keys.length === 0) {
    return <span>{}</span>;
  }

  return (
    <div style={{ paddingLeft: level > 0 ? 20 : 0, width: '100%' }}>
      <span
        onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        {expanded.root ? (
          <T context="collapse indicator icon for JSON viewer object node">▼</T>
        ) : (
          <T context="expand indicator icon for JSON viewer object node">▶</T>
        )}{' '}
        <T>
          Object{' '}
          <Var>{`{${keys.length} ${keys.length === 1 ? 'key' : 'keys'}}`}</Var>
        </T>
      </span>

      {expanded.root && (
        <div style={{ paddingLeft: 20, width: '100%' }}>
          {keys.map((key) => (
            <div
              key={key}
              style={{
                wordBreak: 'break-word',
                marginBottom: 2,
                width: '100%',
              }}
            >
              <span
                style={{ color: '#673ab7', fontWeight: 'bold', marginRight: 8 }}
              >
                {key}:
              </span>
              <JsonViewer data={data[key]} level={level + 1} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FamousShipmentModal: React.FC<Props> = ({
  famousShipment,
  onClose,
  isFetching,
}) => {
  const gt = useGT();
  const [showRawData, setShowRawData] = useState(false);
  const [showForceRefreshDialog, setShowForceRefreshDialog] = useState(false);
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const isAdminUser = isAdmin(currentUser, viewMode);
  const queryClient = useQueryClient();

  const sendEdi856Transaction = useSendEdi856Transaction(
    () => onClose(true),
    () => queryClient.invalidateQueries({ queryKey: ['famous/shipments'] }),
  );

  const syncShipment = useSyncFamousShipment(famousShipment.id, () =>
    queryClient.invalidateQueries({ queryKey: ['famous/shipments'] }),
  );

  const hasBeenSent = !!famousShipment.edi856SentAt;

  const handleSendEdi = (force = false) => {
    sendEdi856Transaction.mutate({
      famousShipmentId: famousShipment.id,
      force,
    });
  };

  const handleSyncShipment = () => {
    // If the ASN was already sent, an admin can force-refresh — but only
    // after confirming. Non-admins never hit this path (button disabled).
    if (hasBeenSent) {
      setShowForceRefreshDialog(true);
      return;
    }
    syncShipment.mutate({});
  };

  const handleConfirmForceRefresh = () => {
    setShowForceRefreshDialog(false);
    syncShipment.mutate({ force: true });
  };

  const handleRefreshStatus = () => {
    queryClient.invalidateQueries({
      queryKey: [`famous/shipments/${famousShipment.id}`],
    });
  };

  const orderDetails = famousShipment.shipmentDetails;
  const customer = orderDetails?.customer;
  const shipTo = orderDetails?.shipTo;
  const carrier = orderDetails?.carrier;

  // Check for translate issues
  const linesWithProducts =
    orderDetails?.lines?.filter((line) => !!line.product) || [];
  const linesWithTranslateIssues = linesWithProducts.filter(
    (line) =>
      !line.product?.translations || line.product.translations.length === 0,
  );
  const hasTranslateIssues = linesWithTranslateIssues.length > 0;
  const missingTranslateProductIds = linesWithTranslateIssues
    .map((line) => line.product?.productIdx)
    .filter(Boolean);

  const missingMessage = (
    <MDBox
      sx={{
        p: 1,
        backgroundColor: '#fff3cd',
        borderRadius: 1,
        border: '1px solid #ffeaa7',
      }}
    >
      <MDTypography variant="caption" color="warning.main">
        <T>Data not available</T>
      </MDTypography>
    </MDBox>
  );

  return (
    <Modal
      maxWidth="xl"
      open={true}
      title={
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          gap={2}
        >
          <MDTypography variant="h6">
            <T>
              Famous Shipment #<Var>{famousShipment.famousShipmentId}</Var>
            </T>
          </MDTypography>
          <MDBox display="flex" alignItems="center" gap={1} sx={{ mr: 5 }}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <MDButton
                    variant="text"
                    color="dark"
                    onClick={handleSyncShipment}
                    disabled={
                      (hasBeenSent && !isAdminUser) || syncShipment.isLoading
                    }
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    {syncShipment.isLoading ? (
                      <HourglassIcon size={24} />
                    ) : (
                      <ArrowsClockwiseIcon size={24} />
                    )}
                  </MDButton>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {hasBeenSent && !isAdminUser ? (
                  <T>
                    Cannot refresh: this ASN has already been sent. Refresh is
                    only available before send.
                  </T>
                ) : hasBeenSent && isAdminUser ? (
                  <T>
                    Admin: force-refresh this already-sent ASN (requires
                    confirmation)
                  </T>
                ) : (
                  <T>Refresh order data from Famous</T>
                )}
              </TooltipContent>
            </Tooltip>
          </MDBox>
        </MDBox>
      }
      closeable
      onCloseButton={{
        text: 'Close',
        onClick: () => onClose(),
        disabled: sendEdi856Transaction.isLoading || syncShipment.isLoading,
      }}
    >
      <div className="relative grid h-full grid-cols-12 gap-6">
        {/* Loading overlay */}
        {syncShipment.isLoading && (
          <div className="absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-white/80">
            <LoadingSpinner
              size="xl"
              text="Syncing shipment data from Famous..."
              centered
            />
          </div>
        )}

        {/* Error Details - Full Width at Top */}
        {famousShipment.shipmentFetchError && (
          <div className="col-span-12">
            <MDBox
              sx={{
                p: 2,
                backgroundColor: '#ffebee',
                borderRadius: 1,
                border: '1px solid #ffcdd2',
              }}
            >
              <MDTypography
                variant="caption"
                color="error.main"
                fontWeight="bold"
                sx={{ display: 'block', mb: 1 }}
              >
                <T context="error details section heading">⚠️ Error Details:</T>
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '10px', wordBreak: 'break-word' }}
              >
                {JSON.stringify(famousShipment.shipmentFetchError, null, 2)}
              </MDTypography>
            </MDBox>
          </div>
        )}

        <div className="col-span-8 flex h-full flex-col overflow-auto">
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            p={2}
            sx={{
              borderBottom: '1px solid #e0e0e0',
              flexShrink: 0,
            }}
          >
            <MDTypography variant="h6" fontWeight="bold">
              <T context="section heading for shipment details">
                Shipment Details
              </T>
            </MDTypography>
          </MDBox>

          <MDBox
            sx={{
              width: '100%',
              overflow: 'auto',
              p: 2,
            }}
          >
            {/* Shipment Summary Section */}
            {orderDetails && (
              <MDBox
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fafafa',
                  flexShrink: 0,
                }}
              >
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  <T context="section heading for shipment summary">
                    Shipment Summary
                  </T>
                </MDTypography>

                <div className="grid grid-cols-2 gap-4">
                  {/* Shipment Information */}
                  <div>
                    <MDBox mb={2}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        mb={1}
                      >
                        <T context="subsection heading for shipment information">
                          Shipment Information
                        </T>
                      </MDTypography>
                      <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                        {orderDetails.orderNumber && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              <T context="label for order number field">
                                Order #:
                              </T>
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.orderNumber}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.purchaseOrderNumber && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              <T context="label for purchase order number field">
                                PO #:
                              </T>
                            </MDTypography>
                            {famousShipment.salesOrderId ? (
                              <MDTypography
                                component="a"
                                href={`/sales-orders-v2?salesOrderId=${famousShipment.salesOrderId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="caption"
                                ml={0.5}
                                sx={{
                                  color: 'info.main',
                                  textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                {orderDetails.purchaseOrderNumber}
                              </MDTypography>
                            ) : (
                              <MDTypography
                                component="span"
                                variant="caption"
                                ml={0.5}
                              >
                                {orderDetails.purchaseOrderNumber}
                              </MDTypography>
                            )}
                          </MDBox>
                        )}
                        {orderDetails.orderDate && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              <T context="label for order date field">
                                Order Date:
                              </T>
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.orderDate}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.shipDateTime && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              <T context="label for ship date field">
                                Ship Date:
                              </T>
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.shipDateTime}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.invoiceDate && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              <T context="label for invoice date field">
                                Invoice Date:
                              </T>
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.invoiceDate}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.etaDate && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              <T context="label for delivery date field">
                                Delivery Date:
                              </T>
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.etaDate}
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                    </MDBox>

                    {/* Carrier Information */}
                    <MDBox mb={2}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        mb={1}
                      >
                        <T context="subsection heading for carrier information">
                          Carrier Information
                        </T>
                      </MDTypography>
                      <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                        {carrier?.name && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              <T context="label for carrier name field">
                                Carrier:
                              </T>
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {carrier.name}
                            </MDTypography>
                          </MDBox>
                        )}
                        {carrier?.SCAC && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              <T context="label for SCAC code field">SCAC:</T>
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {carrier.SCAC}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.truckLicense && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              <T context="label for truck license field">
                                Truck License:
                              </T>
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.truckLicense}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.carrierName && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              <T context="label for carrier name field in order details">
                                Carrier Name:
                              </T>
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.carrierName}
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                    </MDBox>
                  </div>

                  {/* Customer and Shipping Information */}
                  <div>
                    {/* Customer Information */}
                    <MDBox mb={2}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        mb={1}
                      >
                        <T context="subsection heading for customer information">
                          Customer Information
                        </T>
                      </MDTypography>
                      <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                        {customer ? (
                          <>
                            {customer.name && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  <T context="label for customer name field">
                                    Name:
                                  </T>
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {customer.name}
                                </MDTypography>
                              </MDBox>
                            )}
                            {customer.street && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  <T context="label for customer address field">
                                    Address:
                                  </T>
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {customer.street}
                                </MDTypography>
                              </MDBox>
                            )}
                            {customer.city && customer.state && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  <T context="label for customer location field">
                                    Location:
                                  </T>
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {customer.city}, {customer.state}{' '}
                                  {customer.zip}
                                </MDTypography>
                              </MDBox>
                            )}
                          </>
                        ) : (
                          missingMessage
                        )}
                      </MDBox>
                    </MDBox>

                    {/* Ship To Information */}
                    <MDBox mb={2}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        mb={1}
                      >
                        <T context="subsection heading for ship-to information">
                          Ship To Information
                        </T>
                      </MDTypography>
                      <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                        {shipTo ? (
                          <>
                            {shipTo.name && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  <T context="label for ship-to name field">
                                    Name:
                                  </T>
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {shipTo.name}
                                </MDTypography>
                              </MDBox>
                            )}
                            {shipTo.street && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  <T context="label for ship-to address field">
                                    Address:
                                  </T>
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {shipTo.street}
                                </MDTypography>
                              </MDBox>
                            )}
                            {shipTo.city && shipTo.state && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  <T context="label for ship-to location field">
                                    Location:
                                  </T>
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {shipTo.city}, {shipTo.state}, {shipTo.zip}
                                </MDTypography>
                              </MDBox>
                            )}
                          </>
                        ) : (
                          missingMessage
                        )}
                      </MDBox>
                    </MDBox>
                  </div>
                </div>

                {/* Line Items Summary */}
                <MDBox mt={3}>
                  <MDTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    color="primary"
                    mb={2}
                  >
                    <T>
                      Shipment Items (<Var>{linesWithProducts.length}</Var>)
                    </T>
                  </MDTypography>

                  <MDBox
                    sx={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    {linesWithProducts.length > 0 ? (
                      <MDBox sx={{ display: 'flex', flexDirection: 'column' }}>
                        {linesWithProducts.map((line, index) => (
                          <MDBox
                            key={index}
                            sx={{
                              p: 2,
                              borderBottom:
                                index < linesWithProducts.length - 1
                                  ? '1px solid #f0f0f0'
                                  : 'none',
                              '&:hover': {
                                backgroundColor: '#f9f9f9',
                              },
                            }}
                          >
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <MDBox flex={1}>
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  mb={1}
                                >
                                  <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{ fontSize: '11px' }}
                                  >
                                    {line.name || (
                                      <T context="fallback label when product name is missing">
                                        Product
                                      </T>
                                    )}
                                  </MDTypography>
                                  {line.product &&
                                  (!line.product.translations ||
                                    line.product.translations.length === 0) ? (
                                    <MDTypography
                                      variant="caption"
                                      sx={{
                                        backgroundColor: '#fff3cd',
                                        color: '#856404',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: '4px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      <T context="warning badge indicating missing translation data">
                                        ⚠️ No Translate Data
                                      </T>
                                    </MDTypography>
                                  ) : null}
                                </MDBox>

                                {line.product && (
                                  <MDBox sx={{ fontSize: '10px', mb: 1 }}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block' }}
                                    >
                                      <T>
                                        Product SKU:{' '}
                                        <Var>
                                          {line.product.translations?.[0]
                                            ?.externalSku ?? 'N/A'}
                                        </Var>
                                      </T>
                                    </MDTypography>
                                    {line.product.commodity && (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block' }}
                                      >
                                        <T>
                                          Commodity:{' '}
                                          <Var>{line.product.commodity}</Var>
                                        </T>
                                      </MDTypography>
                                    )}
                                    {line.product.variety && (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block' }}
                                      >
                                        <T>
                                          Variety:{' '}
                                          <Var>{line.product.variety}</Var>
                                        </T>
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                )}
                              </MDBox>

                              <MDBox
                                textAlign="right"
                                sx={{ minWidth: '120px' }}
                              >
                                <MDTypography
                                  variant="caption"
                                  sx={{ fontSize: '11px' }}
                                >
                                  <T>
                                    <Var>{line.quantity?.toLocaleString()}</Var>{' '}
                                    <Var>{line.unitOfMeasure || 'units'}</Var>
                                  </T>
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          </MDBox>
                        ))}
                      </MDBox>
                    ) : (
                      <MDBox p={3} textAlign="center">
                        <MDTypography variant="caption" color="text.secondary">
                          <T>No line items available</T>
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                </MDBox>
              </MDBox>
            )}

            {/* Raw Data Accordion */}
            <MDBox
              sx={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <MDBox
                onClick={() => setShowRawData(!showRawData)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  borderRadius: '4px',
                  flexShrink: 0,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                {showRawData ? (
                  <CaretUpIcon size={18} />
                ) : (
                  <CaretDownIcon size={18} />
                )}
                <MDTypography variant="caption" color="text.secondary">
                  <T context="label for accordion toggle that shows raw technical sales order data">
                    Show Raw Sales Order Data (Technical)
                  </T>
                </MDTypography>
              </MDBox>

              {showRawData && (
                <MDBox
                  sx={{
                    display: 'block',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    p: 1,
                    backgroundColor: '#fafafa',
                    flex: 1,
                    overflow: 'auto',
                    minHeight: 0,
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    lineHeight: 1.4,
                  }}
                >
                  <JsonViewer
                    data={
                      famousShipment.shipmentDetails as unknown as JsonValue
                    }
                  />
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </div>

        {/* Right sidebar for EDI status and actions */}
        <div className="col-span-4 flex h-full flex-col">
          <MDBox sx={{ p: 2 }}>
            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
              <MDTypography variant="subtitle2" fontWeight="bold">
                <T context="section heading for EDI 856 status panel">
                  EDI 856 Status
                </T>
              </MDTypography>
              {isFetching && <Spinner size="sm" />}
            </MDBox>

            <SendEdi856Button
              famousShipment={famousShipment}
              sendEdi856Transaction={sendEdi856Transaction}
              onSendEdi={handleSendEdi}
              onRefreshStatus={handleRefreshStatus}
              hasTranslateIssues={hasTranslateIssues}
              missingTranslateProductIds={missingTranslateProductIds}
              isLoading={syncShipment.isLoading}
              isRefreshingStatus={isFetching}
            />

            <MDBox
              sx={{
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 1,
                border: '1px solid #e9ecef',
              }}
            >
              <MDTypography
                variant="caption"
                color="text.secondary"
                fontWeight="bold"
                sx={{ display: 'block', mb: 1 }}
              >
                <T context="heading for informational section about EDI 856">
                  About EDI 856
                </T>
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '11px', lineHeight: 1.4 }}
              >
                <T>
                  EDI 856 transactions notify customers about shipment status,
                  carrier information, and tracking details. This helps
                  customers track their shipments and plan for receipt.
                </T>
              </MDTypography>
            </MDBox>
          </MDBox>
        </div>
      </div>
      <ConfirmDialog
        open={showForceRefreshDialog}
        onClose={() => setShowForceRefreshDialog(false)}
        onConfirm={handleConfirmForceRefresh}
        title={gt('Refresh already-sent ASN?', {
          context:
            'confirmation dialog title when an admin tries to force-refresh a shipment ASN that was already sent',
        })}
        message={(() => {
          const sentAt = famousShipment.edi856SentAt;
          const when = sentAt ? ` on ${new Date(sentAt).toLocaleString()}` : '';
          return `This ASN was already sent${when}. Refreshing will re-fetch fresh data from Famous and clear the sent state so it can be re-transmitted. The previous send record will be lost.`;
        })()}
        confirmText="Refresh and clear sent state"
        dangerous
        loading={syncShipment.isLoading}
      />
    </Modal>
  );
};

// ============================================================================
// SendEdi856Button Component
// ============================================================================

interface SendEdi856ButtonProps {
  famousShipment: FamousShipmentDTO;
  sendEdi856Transaction: ReturnType<typeof useSendEdi856Transaction>;
  onSendEdi: (force?: boolean) => void;
  onRefreshStatus: () => void;
  hasTranslateIssues: boolean;
  missingTranslateProductIds: (number | undefined)[];
  isLoading: boolean;
  isRefreshingStatus?: boolean;
}

function SendEdi856Button({
  famousShipment,
  sendEdi856Transaction,
  onSendEdi,
  onRefreshStatus,
  hasTranslateIssues,
  missingTranslateProductIds,
  isLoading,
  isRefreshingStatus,
}: SendEdi856ButtonProps) {
  const sentAt = famousShipment.edi856SentAt;
  const statusRaw = famousShipment.edi856Status;
  const isError = statusRaw === 'error';
  const isPending = statusRaw === 'pending';
  const isSent = statusRaw === 'sent';
  const transactionId = famousShipment.edi856TransactionId;
  const errorMessage = famousShipment.edi856Error;
  const validationErrors = famousShipment.edi856ValidationErrors ?? [];
  const hasValidationErrors = validationErrors.length > 0;
  // Drop the raw JSON blob from the text message when we render the structured
  // errors below, so the banner text stays readable.
  const displayErrorMessage =
    hasValidationErrors && errorMessage
      ? errorMessage.split(VALIDATION_ERRORS_SUFFIX)[0]
      : errorMessage;
  const isDevelopment = config.isDev;

  function renderSentStatusUi() {
    return (
      <MDBox>
        <MDBox
          sx={{
            p: 2,
            backgroundColor: '#e8f5e8',
            borderRadius: 1,
            border: '1px solid #c8e6c9',
            mb: 2,
          }}
        >
          <MDTypography
            variant="body2"
            fontWeight="bold"
            color="success.main"
            mb={1}
          >
            <T context="status indicator showing EDI 856 was successfully sent">
              ✓ EDI 856 Sent
            </T>
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            <T>
              Sent:{' '}
              <Var>{sentAt ? new Date(sentAt).toLocaleDateString() : ''}</Var>{' '}
              at{' '}
              <Var>
                {sentAt
                  ? new Date(sentAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : ''}
              </Var>
            </T>
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            <T>
              Transaction ID: <Var>{transactionId}</Var>
            </T>
          </MDTypography>
        </MDBox>

        <MDButton
          variant="outlined"
          color="primary"
          onClick={() => {
            window.open(
              `https://ui.orderful.com/transactions/${transactionId}`,
              '_blank',
              'noopener,noreferrer',
            );
          }}
          fullWidth
          sx={{ mb: 2 }}
        >
          <T context="button label to view transaction in Orderful">
            View in Orderful
          </T>
        </MDButton>
      </MDBox>
    );
  }

  function renderErrorStatusUi() {
    return (
      <MDBox
        sx={{
          p: 2,
          backgroundColor: '#ffebee',
          borderRadius: 1,
          border: '1px solid #ffcdd2',
          mb: 2,
        }}
      >
        <MDTypography
          variant="body2"
          fontWeight="bold"
          color="error.main"
          mb={1}
        >
          <T context="status indicator showing EDI 856 failed">
            EDI 856 Failed
          </T>
        </MDTypography>
        {sentAt && (
          <MDTypography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            <T>
              Last attempt: <Var>{new Date(sentAt).toLocaleDateString()}</Var>{' '}
              at{' '}
              <Var>
                {new Date(sentAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Var>
            </T>
          </MDTypography>
        )}
        {errorMessage && (
          <MDTypography
            variant="caption"
            color="error"
            sx={{ display: 'block' }}
          >
            {displayErrorMessage}
          </MDTypography>
        )}
        {hasValidationErrors && (
          <MDBox sx={{ mt: 1.5 }}>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              color="error.main"
              sx={{ display: 'block', mb: 0.5 }}
            >
              <T context="header above the list of Orderful EDI validation errors">
                Validation errors from Orderful
              </T>
            </MDTypography>
            {validationErrors.map(
              (validationError: OrderfulValidationError, index: number) => {
                const { field, trail } = describeEdiDataPath(
                  validationError.dataPath,
                );
                return (
                  <MDBox
                    key={`${validationError.dataPath ?? 'error'}-${index}`}
                    sx={{
                      mb: 1,
                      pl: 1,
                      borderLeft: '2px solid #ef9a9a',
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      color="error"
                      sx={{ display: 'block', fontWeight: 'bold' }}
                    >
                      {field ?? validationError.dataPath ?? '—'}
                      {validationError.validationErrorType
                        ? ` (${validationError.validationErrorType})`
                        : ''}
                    </MDTypography>
                    {validationError.message && (
                      <MDTypography
                        variant="caption"
                        color="text.primary"
                        sx={{ display: 'block' }}
                      >
                        {validationError.message}
                      </MDTypography>
                    )}
                    {trail && (
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          fontFamily: 'monospace',
                          fontSize: '0.65rem',
                        }}
                      >
                        {trail}
                      </MDTypography>
                    )}
                  </MDBox>
                );
              },
            )}
          </MDBox>
        )}
        {transactionId && (
          <MDBox sx={{ mt: 2 }}>
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              <T>
                Transaction ID: <Var>{transactionId}</Var>
              </T>
            </MDTypography>
            <MDButton
              variant="outlined"
              color="primary"
              onClick={() => {
                window.open(
                  `https://ui.orderful.com/transactions/${transactionId}`,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
              fullWidth
            >
              <T context="button label to view transaction in Orderful">
                View in Orderful
              </T>
            </MDButton>
          </MDBox>
        )}
      </MDBox>
    );
  }

  function renderPendingStatusUi() {
    return (
      <MDBox>
        <MDBox
          sx={{
            p: 2,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #90caf9',
            mb: 2,
          }}
        >
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <MDTypography variant="body2" fontWeight="bold" color="info.main">
              <T context="status indicator showing EDI 856 is pending">
                EDI 856 Pending
              </T>
            </MDTypography>
            <MDButton
              variant="text"
              color="dark"
              onClick={onRefreshStatus}
              disabled={isRefreshingStatus}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              {isRefreshingStatus ? (
                <HourglassIcon size={18} />
              ) : (
                <ArrowsClockwiseIcon size={18} />
              )}
            </MDButton>
          </MDBox>
          {sentAt && (
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              <T>
                Submitted: <Var>{new Date(sentAt).toLocaleDateString()}</Var> at{' '}
                <Var>
                  {new Date(sentAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Var>
              </T>
            </MDTypography>
          )}
          {transactionId && (
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              <T>
                Transaction ID: <Var>{transactionId}</Var>
              </T>
            </MDTypography>
          )}
        </MDBox>

        {transactionId && (
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => {
              window.open(
                `https://ui.orderful.com/transactions/${transactionId}`,
                '_blank',
                'noopener,noreferrer',
              );
            }}
            fullWidth
            sx={{ mb: 2 }}
          >
            <T context="button label to view transaction in Orderful">
              View in Orderful
            </T>
          </MDButton>
        )}
      </MDBox>
    );
  }

  function renderSendButtonUi(forceResend = false) {
    return (
      <MDBox>
        <MDButton
          variant="contained"
          color={forceResend ? 'warning' : 'primary'}
          onClick={() => onSendEdi(forceResend)}
          disabled={
            sendEdi856Transaction.isLoading || hasTranslateIssues || isLoading
          }
          fullWidth
          sx={{ mb: 2 }}
        >
          {sendEdi856Transaction.isLoading ? (
            <T context="button label while EDI 856 is being sent">Sending...</T>
          ) : hasTranslateIssues ? (
            <T context="button label when send is blocked due to missing translate data">
              Cannot Send - Missing Translate Data
            </T>
          ) : forceResend ? (
            <T context="button label to resend an EDI 856 transaction">
              Resend EDI 856 Transaction
            </T>
          ) : isError ? (
            <T context="button label to retry a failed EDI 856 transaction">
              Retry EDI 856 Transaction
            </T>
          ) : (
            <T context="button label to send an EDI 856 transaction">
              Send EDI 856 Transaction
            </T>
          )}
        </MDButton>

        {hasTranslateIssues && (
          <MDBox
            sx={{
              p: 2,
              backgroundColor: '#fff3cd',
              borderRadius: 1,
              border: '1px solid #ffeaa7',
              mb: 2,
            }}
          >
            <MDTypography
              variant="caption"
              color="warning.main"
              fontWeight="bold"
            >
              <T context="warning heading indicating EDI cannot be sent due to missing translate data">
                Cannot Send EDI: Missing translate data
              </T>
            </MDTypography>
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1 }}
            >
              <T>
                Product IDs missing translate data:{' '}
                <Var>
                  {missingTranslateProductIds.filter(Boolean).join(', ')}
                </Var>
              </T>
            </MDTypography>
          </MDBox>
        )}

        {sendEdi856Transaction.error && (
          <MDBox
            sx={{
              p: 2,
              backgroundColor: '#ffebee',
              borderRadius: 1,
              border: '1px solid #ffcdd2',
            }}
          >
            <MDTypography variant="caption" color="error">
              <T>
                Error:{' '}
                <Var>
                  {sendEdi856Transaction.error?.message ||
                    'Failed to send EDI 856 transaction'}
                </Var>
              </T>
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    );
  }

  function renderDevelopmentUi() {
    if (isSent) {
      return (
        <MDBox>
          {renderSentStatusUi()}
          <AlertContainer className="mb-2 border-amber-200 bg-amber-50">
            <AlertDescription className="text-amber-800">
              <T>
                Development mode: You can resend the EDI 856 transaction. This
                will create a new transaction in Orderful.
              </T>
            </AlertDescription>
          </AlertContainer>
          {renderSendButtonUi(true)}
        </MDBox>
      );
    }
    if (isPending) {
      return <MDBox>{renderPendingStatusUi()}</MDBox>;
    }
    if (isError) {
      return (
        <MDBox>
          {renderErrorStatusUi()}
          {renderSendButtonUi(false)}
        </MDBox>
      );
    }
    return renderSendButtonUi(false);
  }

  function renderProductionUi() {
    if (isSent) {
      return renderSentStatusUi();
    }
    if (isPending) {
      return renderPendingStatusUi();
    }
    if (isError) {
      return (
        <MDBox>
          {renderErrorStatusUi()}
          {renderSendButtonUi(false)}
        </MDBox>
      );
    }
    return renderSendButtonUi();
  }

  return isDevelopment ? renderDevelopmentUi() : renderProductionUi();
}

export default FamousShipmentModal;
